/**
 * Safe print utility - opens a new window and renders content
 * using only DOM-safe APIs (DOMParser, adoptNode, appendChild).
 *
 * Uses DOMPurify to sanitize, then DOMParser to parse into a
 * real DOM tree, then adoptNode to move nodes into the print window.
 */
import DOMPurify from 'dompurify';

/**
 * Safely render HTML into a print window using DOM manipulation only.
 * @param {string} htmlContent - Full HTML document string to render
 * @returns {Window|null}
 */
export const safePrintHTML = (htmlContent) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.warn('Print window blocked by browser popup blocker');
    return null;
  }

  writeSanitizedHTML(printWindow.document, htmlContent);

  printWindow.focus();
  setTimeout(() => printWindow.print(), 500);
  return printWindow;
};

/**
 * Write sanitized HTML into a target document using safe DOM APIs.
 * Replaces unsafe DOM injection patterns with safe node adoption.
 *
 * @param {Document} targetDoc - The document to write into (e.g. printWindow.document)
 * @param {string} rawHTML - Raw HTML content string
 */
export const writeSanitizedHTML = (targetDoc, rawHTML) => {
  // Step 1: Sanitize with DOMPurify
  const clean = DOMPurify.sanitize(rawHTML, {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ['style', 'link', 'meta', 'title', 'head', 'body', 'html'],
    ADD_ATTR: ['target', 'media', 'class', 'colspan', 'rowspan'],
  });

  // Step 2: Parse the sanitized string into a real DOM tree
  const parser = new DOMParser();
  const parsed = parser.parseFromString(clean, 'text/html');

  // Step 3: Replace the target document's content using DOM node adoption
  // Clear existing content
  while (targetDoc.head.firstChild) {
    targetDoc.head.removeChild(targetDoc.head.firstChild);
  }
  while (targetDoc.body.firstChild) {
    targetDoc.body.removeChild(targetDoc.body.firstChild);
  }

  // Adopt and append head nodes
  Array.from(parsed.head.childNodes).forEach(node => {
    targetDoc.head.appendChild(targetDoc.adoptNode(node.cloneNode(true)));
  });

  // Adopt and append body nodes
  Array.from(parsed.body.childNodes).forEach(node => {
    targetDoc.body.appendChild(targetDoc.adoptNode(node.cloneNode(true)));
  });
};

export default safePrintHTML;
